import { useTranslation } from 'react-i18next';
import {
  Icon,
  IconNames,
  LoadingIndicator,
  MessageBlock,
  MessageBlockType,
  PrimeTable,
  SpinnerSizes,
} from '@triumph/design-system';
import { useAppContext } from '@/app/components/providers/AppContextProvider';
import { resolveCarrierBadge } from '@/app/shared/utils/noaUtils';
import { formatShortDate } from '@/app/shared/utils/tableUtils';
import { Carrier } from '../noaCenter.types';
import { StatusBadge } from './StatusBadge';
import {
  CarrierIconBox,
  CarrierRowContent,
  CarrierRowInfo,
  CarrierRowMeta,
  CarrierRowName,
  CarrierRowTrailing,
  CarrierTableWrapper,
  ResultsCount,
  ResultsHeader,
  ResultsPanel,
  ResultsTitle,
  StateWrapper,
} from '../NOACenter.StyledComponents';

interface CarrierListProps {
  carriers: Carrier[];
  selectedCarrierId: string | null;
  onSelect: (carrierId: string) => void;
  isLoading: boolean;
  hasSearched: boolean;
}

export const CarrierList: React.FC<CarrierListProps> = ({
  carriers,
  selectedCarrierId,
  onSelect,
  isLoading,
  hasSearched,
}) => {
  const { t } = useTranslation('noaCenter');
  const { theme } = useAppContext();

  const carrierBody = (carrier: Carrier) => {
    const selected = carrier.carrierId === selectedCarrierId;
    return (
      <CarrierRowContent>
        <CarrierIconBox selected={selected}>
          <Icon
            name={IconNames.Office_OfficeBuilding}
            id={`noa-carrier-icon-${carrier.carrierId}`}
            size="sm"
            color={selected ? theme.colors.greyscale.white : theme.colors.greyscale.gray40}
          />
        </CarrierIconBox>
        <CarrierRowInfo>
          <CarrierRowName>{carrier.carrierName}</CarrierRowName>
          <CarrierRowMeta>
            {carrier.mcNumber && <span>MC# {carrier.mcNumber}</span>}
            {carrier.dotNumber && <span>DOT# {carrier.dotNumber}</span>}
            {carrier.lastUpdated && (
              <span>{t('list.updated', { date: formatShortDate(carrier.lastUpdated) })}</span>
            )}
          </CarrierRowMeta>
        </CarrierRowInfo>
        <CarrierRowTrailing>
          <StatusBadge badge={resolveCarrierBadge(carrier)} />
          <Icon
            name={IconNames.Chevron_Right}
            id={`noa-carrier-chevron-${carrier.carrierId}`}
            size="sm"
            color={theme.colors.greyscale.gray30}
          />
        </CarrierRowTrailing>
      </CarrierRowContent>
    );
  };

  const columns = [{ field: 'carrierName', header: '', body: carrierBody }];

  const renderBody = () => {
    if (isLoading) {
      return (
        <StateWrapper data-testid="noa-carrier-loading">
          <LoadingIndicator
            show
            size={SpinnerSizes.regular}
            message={t('list.loading')}
          />
        </StateWrapper>
      );
    }

    if (hasSearched && carriers.length === 0) {
      return (
        <StateWrapper data-testid="noa-no-results">
          <MessageBlock
            message={t('emptyStates.noResults')}
            type={MessageBlockType.info}
            messageId={0}
            width="auto"
          />
        </StateWrapper>
      );
    }

    return (
      <CarrierTableWrapper data-testid="noa-carrier-table">
        <PrimeTable
          columns={columns}
          value={carriers}
          dataKey="carrierId"
          selectionMode="single"
          selection={carriers.find((c) => c.carrierId === selectedCarrierId) ?? null}
          onSelectionChange={(e: { value: Carrier | null }) =>
            e.value && onSelect(e.value.carrierId)
          }
          rowClassName={(row: Carrier) =>
            row.carrierId === selectedCarrierId ? 'carrier-row--selected' : ''
          }
          scrollable={false}
          alwaysShowPaginator={false}
          loading={isLoading}
        />
      </CarrierTableWrapper>
    );
  };

  return (
    <ResultsPanel data-testid="noa-results-panel">
      <ResultsHeader>
        <ResultsTitle>{t('list.title')}</ResultsTitle>
        <ResultsCount data-testid="noa-results-count">
          {t('list.results', { count: carriers.length })}
        </ResultsCount>
      </ResultsHeader>
      {renderBody()}
    </ResultsPanel>
  );
};
